/**
 * Product Card Image Treatment
 * Applies a WebGL shader-based image treatment to the hover state of product cards.
 * On hover, the untreated image fades out to reveal the treated version beneath.
 */
(function () {
  const VERTEX_SHADER = `
    attribute vec2 a_position;
    attribute vec2 a_texCoord;
    varying vec2 v_texCoord;
    void main() {
      gl_Position = vec4(a_position, 0.0, 1.0);
      v_texCoord = a_texCoord;
    }
  `;

  const FRAGMENT_SHADER = `
    precision mediump float;

    uniform sampler2D u_image;
    uniform vec2 u_resolution;

    // Levels
    uniform float u_inBlack;
    uniform float u_inWhite;
    uniform float u_gamma;
    uniform float u_outBlack;
    uniform float u_outWhite;

    // Gradient Map
    uniform vec3 u_shadowColor;
    uniform vec3 u_highlightColor;
    uniform float u_centerPoint;

    // Noise
    uniform float u_noiseOpacity;
    uniform float u_noiseScale;
    uniform float u_noiseSeed;

    varying vec2 v_texCoord;

    vec3 softLight(vec3 base, vec3 blend) {
      vec3 result;
      for (int i = 0; i < 3; i++) {
        float b = blend[i];
        float a = base[i];
        if (b < 0.5) {
          result[i] = a - (1.0 - 2.0 * b) * a * (1.0 - a);
        } else {
          float d = a <= 0.25
            ? ((16.0 * a - 12.0) * a + 4.0) * a
            : sqrt(a);
          result[i] = a + (2.0 * b - 1.0) * (d - a);
        }
      }
      return result;
    }

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
    }

    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      f = f * f * (3.0 - 2.0 * f);

      float a = hash(i);
      float b = hash(i + vec2(1.0, 0.0));
      float c = hash(i + vec2(0.0, 1.0));
      float d = hash(i + vec2(1.0, 1.0));

      return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    }

    void main() {
      vec3 color = texture2D(u_image, v_texCoord).rgb;

      // 1. Levels
      vec3 v = clamp((color - u_inBlack) / (u_inWhite - u_inBlack), 0.0, 1.0);
      v = pow(v, vec3(1.0 / u_gamma));
      color = u_outBlack + v * (u_outWhite - u_outBlack);

      // 2. Gradient Map
      float lum = dot(color, vec3(0.299, 0.587, 0.114));
      float cp = u_centerPoint;
      float t = lum < cp
        ? (lum / cp) * 0.5
        : 0.5 + ((lum - cp) / (1.0 - cp)) * 0.5;
      color = mix(u_shadowColor, u_highlightColor, t);

      // 3. Noise (soft light blend)
      vec2 noiseCoord = v_texCoord * u_resolution / 256.0 * u_noiseScale;
      noiseCoord += u_noiseSeed;
      float n = noise(noiseCoord * 8.0);
      n = n * 0.5 + noise(noiseCoord * 16.0) * 0.25 + noise(noiseCoord * 32.0) * 0.25;
      vec3 blended = softLight(color, vec3(n));
      color = mix(color, blended, u_noiseOpacity);

      gl_FragColor = vec4(color, 1.0);
    }
  `;

  // Treatment settings
  const SETTINGS = {
    inBlack: 27 / 255,
    inWhite: 200 / 255,
    gamma: 1.0,
    outBlack: 0 / 255,
    outWhite: 240 / 255,
    shadowColor: [0x21 / 255, 0x21 / 255, 0x1e / 255],
    highlightColor: [0xef / 255, 0xec / 255, 0xe5 / 255],
    centerPoint: 0.3,
    noiseOpacity: 0.4,
    noiseScale: 20,
    noiseSeed: 0,
  };

  function compileShader(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function createProgram(gl, vs, fs) {
    var program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return null;
    }
    return program;
  }

  function initRenderer() {
    var canvas = document.createElement('canvas');
    var gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
    if (!gl) return null;

    var vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    var fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vs || !fs) return null;

    var program = createProgram(gl, vs, fs);
    if (!program) return null;

    // Geometry: full-screen quad
    var posBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    var texBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]),
      gl.STATIC_DRAW
    );

    var posLoc = gl.getAttribLocation(program, 'a_position');
    var texLoc = gl.getAttribLocation(program, 'a_texCoord');

    gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, texBuf);
    gl.enableVertexAttribArray(texLoc);
    gl.vertexAttribPointer(texLoc, 2, gl.FLOAT, false, 0, 0);

    var texture = gl.createTexture();

    return { canvas: canvas, gl: gl, program: program, texture: texture };
  }

  function treatImage(renderer, img) {
    var gl = renderer.gl;
    var canvas = renderer.canvas;
    var program = renderer.program;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.useProgram(program);

    // Upload image texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, renderer.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Set uniforms
    gl.uniform1i(gl.getUniformLocation(program, 'u_image'), 0);
    gl.uniform2f(
      gl.getUniformLocation(program, 'u_resolution'),
      canvas.width,
      canvas.height
    );

    gl.uniform1f(
      gl.getUniformLocation(program, 'u_inBlack'),
      SETTINGS.inBlack
    );
    gl.uniform1f(
      gl.getUniformLocation(program, 'u_inWhite'),
      SETTINGS.inWhite
    );
    gl.uniform1f(gl.getUniformLocation(program, 'u_gamma'), SETTINGS.gamma);
    gl.uniform1f(
      gl.getUniformLocation(program, 'u_outBlack'),
      SETTINGS.outBlack
    );
    gl.uniform1f(
      gl.getUniformLocation(program, 'u_outWhite'),
      SETTINGS.outWhite
    );

    gl.uniform3fv(
      gl.getUniformLocation(program, 'u_shadowColor'),
      SETTINGS.shadowColor
    );
    gl.uniform3fv(
      gl.getUniformLocation(program, 'u_highlightColor'),
      SETTINGS.highlightColor
    );
    gl.uniform1f(
      gl.getUniformLocation(program, 'u_centerPoint'),
      SETTINGS.centerPoint
    );

    gl.uniform1f(
      gl.getUniformLocation(program, 'u_noiseOpacity'),
      SETTINGS.noiseOpacity
    );
    gl.uniform1f(
      gl.getUniformLocation(program, 'u_noiseScale'),
      SETTINGS.noiseScale
    );
    gl.uniform1f(
      gl.getUniformLocation(program, 'u_noiseSeed'),
      SETTINGS.noiseSeed
    );

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    return canvas.toDataURL('image/jpeg', 0.92);
  }

  function loadCorsImage(src, callback) {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      callback(img);
    };
    img.onerror = function () {
      console.error('Image treatment: CORS load failed for', src);
    };
    img.src = src;
  }

  function processCard(renderer, container) {
    var imgs = container.querySelectorAll('img');
    if (imgs.length < 2) return;

    var sourceImg = imgs[0];
    var targetImg = imgs[1];

    function apply() {
      var src = sourceImg.currentSrc || sourceImg.src;
      if (!src) return;

      loadCorsImage(src, function (corsImg) {
        try {
          var treatedSrc = treatImage(renderer, corsImg);
          targetImg.removeAttribute('srcset');
          targetImg.removeAttribute('sizes');
          targetImg.src = treatedSrc;
        } catch (e) {
          console.error('Image treatment failed:', e);
        }
      });
    }

    if (sourceImg.complete && sourceImg.naturalWidth > 0) {
      apply();
    } else {
      sourceImg.addEventListener('load', apply, { once: true });
    }
  }

  function init() {
    var renderer = initRenderer();
    if (!renderer) return;

    var cards = document.querySelectorAll('.product-card__image');
    cards.forEach(function (card) {
      processCard(renderer, card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
